import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { Divider } from 'antd';
import { isEmailValid } from '@zorba-shared/core/Services/textService';
import { ZorbaButton, ZorbaInput } from '../../../../components/forms';
import { updateBuyerDetails } from '../../../../reducers/Buyer/actions';
import {
  BUTTON_VARIANT_DARK,
  BUTTON_VARIANT_LINK,
} from '../../../../components/forms/ZorbaButton/constants';
import EmailInput from '../../../../components/forms/EmailInput';
import PhoneInput from '../../../../components/forms/PhoneInput';
import { getAccountFieldsToEdit } from './constants';

export const Account = () => {
  const { organization, userDetails } = useSelector(
    ({ AuthReducer }) => AuthReducer,
  );
  const { isLoading } = useSelector(({ BuyerReducer }) => BuyerReducer);
  const dispatch = useDispatch();
  const [selectedField, setSelectedField] = useState();
  const [fieldValue, setFieldValue] = useState();
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const fields = getAccountFieldsToEdit({
    name: userDetails.name,
    entityName: organization.entityName,
    email: userDetails.email,
    phoneNumber: userDetails.phoneNumber?.number || '',
  });

  const handleEdit = (field) => {
    setSelectedField(field);
    setFieldValue(field.value);
  };

  const handleOnChange = (value) => {
    setFieldValue(value);
  };

  const handleEmailChange = (emailEvent) => {
    setFieldValue(emailEvent.value);

    if (!isEmailValid(emailEvent.value)) {
      setEmailError('Please provide a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleOnSave = () => {
    const generatePayload = () => {
      const fieldMap = {
        name: (value) => ({ name: value }),
        entityName: (value) => ({ entityName: value }),
        email: (value) => ({ email: value }),
        phoneNumber: (value) => ({
          phoneNumber: {
            ...userDetails.phoneNumber,
            number: value,
            suggestedNumber: `+1${value}`,
          },
        }),
      };

      return fieldMap[selectedField.fieldName]?.(fieldValue) || {};
    };

    dispatch(
      updateBuyerDetails(
        generatePayload(),
        'Updating settings succeeded!',
        'Updating settings failed. Please try again!',
        () => setSelectedField(null),
      ),
    );
  };

  const handleCancel = () => {
    setSelectedField(null);
    setEmailError('');
    setPhoneError('');
  };

  const handlePhoneNumberChange = (phoneEvent) => {
    const isValidNumber = (input) => input?.toString().length === 10;
    setFieldValue(phoneEvent.number);

    if (!isValidNumber(phoneEvent.number)) {
      setPhoneError('Please provide a valid phone number');
    } else {
      setPhoneError('');
    }
  };

  return (
    <>
      {fields.map((field) => (
        <>
          <div key={field.fieldName} className="field-container">
            <div className="description-container">
              <span
                className={`label ${
                  selectedField && selectedField?.fieldName !== field.fieldName
                    ? 'disabled'
                    : ''
                }`}
              >
                {field.label}
              </span>
              {selectedField?.fieldName !== field.fieldName && (
                <span className="value">{field.value}</span>
              )}
            </div>
            <div>
              {selectedField?.fieldName !== field.fieldName && (
                <ZorbaButton
                  variant={BUTTON_VARIANT_LINK}
                  fullWidth={false}
                  className={`button ${
                    selectedField &&
                    selectedField?.fieldName !== field.fieldName
                      ? 'disabled'
                      : ''
                  }`}
                  onClick={() => {
                    handleEdit(field);
                  }}
                  disabled={isLoading}
                >
                  Edit
                </ZorbaButton>
              )}
            </div>
          </div>
          {selectedField?.fieldName === field.fieldName && (
            <div className="form-container">
              <div>
                {selectedField.fieldName === 'email' && (
                  <EmailInput
                    fullWidth
                    label=""
                    defaultValue={fieldValue}
                    onChange={handleEmailChange}
                    errorMessage={emailError}
                    showErrorMessage={emailError}
                    formItemWithoutLabel
                  />
                )}
                {(selectedField.fieldName === 'name' ||
                  selectedField.fieldName === 'entityName') && (
                  <ZorbaInput
                    fullWidth={false}
                    value={fieldValue}
                    onChange={(e) => handleOnChange(e.target.value)}
                  />
                )}
                {selectedField.fieldName === 'phoneNumber' && (
                  <PhoneInput
                    fullWidth
                    label=""
                    defaultValue={fieldValue}
                    onChange={handlePhoneNumberChange}
                    errorMessage={phoneError}
                    showErrorMessage={phoneError}
                  />
                )}
              </div>
              <div className="buttons-container">
                <ZorbaButton
                  fullWidth={false}
                  variant={BUTTON_VARIANT_DARK}
                  onClick={handleOnSave}
                  disabled={
                    !fieldValue || emailError || phoneError || isLoading
                  }
                  loading={isLoading}
                >
                  Save
                </ZorbaButton>
                <ZorbaButton
                  fullWidth={false}
                  variant={BUTTON_VARIANT_LINK}
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </ZorbaButton>
              </div>
            </div>
          )}
          <Divider className="divider" />
        </>
      ))}
    </>
  );
};
